import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  FINANCIAL_VERSION,
  REPORT_EXPANSION,
  SCOPE,
  UNIT_DISPLAY,
  RETAINED_EARNING,
  BUDGET,
  BUDGET_VERSION,
  DOWNLOAD_REPORT,
  OBJECT_TYPE,
  OBJECT_VALUES,
  REPORTING_LEVEL,
  REPORTING_PERIOD,
  FISCAL_YEAR,
  PERIOD_FROM,
  PERIOD_TO,
  DOCUMENT_TYPES,
  GL_MASTERS,
  ACC_CATEGORY,
  LEVELS,
} from "./consts";
import {
  useAddFilterVariantsMutation,
  useGetBudgetByCommunityData,
  useGetDocumentTypeDropdown,
  useGetFilterVariantsById,
  useGetFinancialTemplates,
  useGetFinancialTemplateStructure,
  useGetGlMasterDataDropdown,
  useGetPropertiesByCommunity,
  useGetPropertyBlockByCommunityId,
  useGetPropertyZoneByCommunityId,
  useUpdateFilterVariantsMutation,
} from "@/services";
import axiosAuth from "@/services/axiosAuth";
import { MANAGERIAL_REPORT_API } from "@/services/managerialReport/apiRoutes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/Toast";

const errorMessage = (error?: any) =>
  typeof error?.response?.data?.message === "string" ? error.response.data.message : "Something went wrong";
const toastError = (error?: any) => toast.error(errorMessage(error));
const toastSuccess = (msg: string) => toast.success(msg);
const asArray = (v: any): string[] => (Array.isArray(v) ? v.map(String) : v === null || v === undefined || v === "" ? [] : String(v).split(",").filter(Boolean));

/** Defaults from "Managerial Report.docx": the (default) option of every radio, current fiscal year, full year. */
const defaultCriteria = (communityId?: string, fiscalYear?: any) => ({
  [FINANCIAL_VERSION]: "",
  [REPORT_EXPANSION]: "BY_SUB_GROUPS",
  [SCOPE]: "P_AND_L",
  [UNIT_DISPLAY]: "REPORT_UNIT",
  [RETAINED_EARNING]: "WITHOUT",
  [BUDGET]: "WITH_BUDGET",
  [BUDGET_VERSION]: "",
  [DOWNLOAD_REPORT]: "CSV",
  [OBJECT_TYPE]: "COMMUNITY",
  [OBJECT_VALUES]: communityId ? [String(communityId)] : [],
  [REPORTING_LEVEL]: "COMMUNITY",
  [REPORTING_PERIOD]: "ENTRY_YEAR",
  [FISCAL_YEAR]: fiscalYear ? String(fiscalYear) : "",
  [PERIOD_FROM]: "1",
  [PERIOD_TO]: "12",
  [DOCUMENT_TYPES]: [],
  [GL_MASTERS]: [],
  [ACC_CATEGORY]: [],
});

/**
 * Activity Dashboard > Cost Analytics > Managerial Reporting: criteria (saved / loaded as variants), Run Report
 * and the result view. Unit Display and Download format are presentation-only; everything else goes to
 * GET /managerial-reports/community/{id}/run.
 */
const useManagerialReport = () => {
  const { communityData, currentFinanceYear } = useSelector((state: RootState) => state.currentData);
  const queryClient = useQueryClient();
  const communityName = communityData?.name;
  const communityId = communityData?.communityId as string;

  const form = useForm<any>({ defaultValues: defaultCriteria(communityId, currentFinanceYear) });
  const formData = useWatch({ control: form.control });
  const [openLoadVariantDialog, setOpenLoadVariantDialog] = useState<boolean>(false);
  const [openSaveDialog, setOpenSaveDialog] = useState<boolean>(false);
  const [variantName, setVariantName] = useState<string>("");
  const [result, setResult] = useState<any>(null);
  const [ranWith, setRanWith] = useState<any>(null);
  const selectedVariantId = useRef<any>(null);
  const [loadVariantId, setLoadVariantId] = useState<any>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const breadcrumbData = useMemo(
    () => [
      { link: `/${routes.admin}/${routes.activityDashboard}`, name: `Activity Dashboard - ${communityName}` },
      { link: "", name: "Cost Analytics" },
      { link: "", name: <span className="text-primary">Managerial Reporting</span> },
    ],
    [communityName]
  );

  // ---- dropdown sources ----
  const { data: versions = [], isLoading: isLoadingVersions } = useGetFinancialTemplates({ communityId });
  // Financial Version: only PUBLISHED versions of the community
  const financialVersionOptions = useMemo(
    () => versions.filter((v: any) => v.status === "PUBLISHED")
      .sort((a: any, b: any) => a.title.localeCompare(b.title) || b.versionNo - a.versionNo)
      .map((v: any) => ({ label: `${v.title} (v${v.versionNo})`, value: String(v.financialTemplateId) })),
    [versions]
  );
  const { data: structure } = useGetFinancialTemplateStructure({ financialTemplateId: formData[FINANCIAL_VERSION] || undefined });
  const accountCategoryOptions = useMemo(
    () => (structure?.categories || []).map((c: any) => ({ label: c.name, value: String(c.accountCategoryId) })),
    [structure]
  );

  const { data: budgetData, isLoading: isBudgetDataLoading } = useGetBudgetByCommunityData({
    communityId,
    queryParams: { enabled: !!communityId },
  });
  // Budget version: approved budgets of the selected fiscal year
  const budgetVersionOptions = useMemo(
    () => (budgetData?.data || [])
      .filter((b: any) => b?.status?.toLowerCase() == "approved" && String(b?.year) == String(formData[FISCAL_YEAR]))
      .map((b: any) => ({ label: b.budgetName, value: String(b.budgetId) })),
    [budgetData, formData[FISCAL_YEAR]]
  );
  useEffect(() => {
    // pick the year's approved budget when nothing (or a budget of another year) is selected
    if (formData[BUDGET] !== "WITH_BUDGET") return;
    const current = String(formData[BUDGET_VERSION] || "");
    if (!budgetVersionOptions.some((o: any) => o.value === current)) {
      form.setValue(BUDGET_VERSION, budgetVersionOptions[0]?.value || "");
    }
  }, [budgetVersionOptions, formData[BUDGET]]);
  useEffect(() => {
    if (!formData[FINANCIAL_VERSION] && financialVersionOptions.length) {
      form.setValue(FINANCIAL_VERSION, financialVersionOptions[0].value);
    }
  }, [financialVersionOptions]);

  const { data: docTypeList, isLoading: isLoadingDocTypeList } = useGetDocumentTypeDropdown({
    communityId,
    queryParams: { enabled: !!communityId },
  });
  const { data: glData, isLoading: isLoadingGlMasterList } = useGetGlMasterDataDropdown({ communityId });

  const objectType = formData[OBJECT_TYPE];
  const { data: zoneList, isLoading: isLoadingZoneList } = useGetPropertyZoneByCommunityId({
    communityId, queryParams: { enabled: !!communityId && objectType == "ZONE" },
  });
  const { data: blockList, isLoading: isLoadingBlockList } = useGetPropertyBlockByCommunityId({
    communityId, queryParams: { enabled: !!communityId && objectType == "BLOCK" },
  });
  const { data: propertyList, isLoading: isLoadingPropertyList } = useGetPropertiesByCommunity({
    communityId, queryParams: { enabled: !!communityId && objectType == "PROPERTY" },
  });

  // Object Value: Community = this community (default); Zone / Block / Property = that list
  const objectValues = useMemo(() => {
    if (objectType == "COMMUNITY") return communityId ? [{ label: communityName, value: String(communityId) }] : [];
    if (objectType == "ZONE") return zoneList?.data?.map((d: any) => ({ label: d?.zoneName, value: String(d?.zoneId) })) || [];
    if (objectType == "BLOCK") return blockList?.data?.map((d: any) => ({ label: d?.blockName, value: String(d?.blockId) })) || [];
    if (objectType == "PROPERTY") return propertyList?.data?.map((d: any) => ({ label: d?.name, value: String(d?.propertyId) })) || [];
    return [];
  }, [objectType, communityId, communityName, zoneList, blockList, propertyList]);

  // Report Level is always the object type or below it (Community > Zone > Block > Property)
  const reportLevelOptions = useMemo(
    () => LEVELS.slice(Math.max(LEVELS.indexOf(objectType), 0)).map((l) => ({ value: l, label: l.charAt(0) + l.slice(1).toLowerCase() })),
    [objectType]
  );
  const previousObjectType = useRef(objectType);
  useEffect(() => {
    if (previousObjectType.current !== objectType) {
      form.setValue(OBJECT_VALUES, objectType == "COMMUNITY" && communityId ? [String(communityId)] : []);
      previousObjectType.current = objectType;
    }
    if (LEVELS.indexOf(formData[REPORTING_LEVEL]) < LEVELS.indexOf(objectType)) {
      form.setValue(REPORTING_LEVEL, objectType);
    }
  }, [objectType]);
  useEffect(() => {
    if (Number(formData[PERIOD_TO]) < Number(formData[PERIOD_FROM])) form.setValue(PERIOD_TO, formData[PERIOD_FROM]);
  }, [formData[PERIOD_FROM]]);

  // ---- run ----
  const buildParams = (d: any) => {
    const p = new URLSearchParams();
    const set = (k: string, v: any) => v !== undefined && v !== null && v !== "" && p.set(k, String(v));
    set("financialTemplateId", d[FINANCIAL_VERSION]);
    set("reportExpansion", d[REPORT_EXPANSION]);
    set("scope", d[SCOPE]);
    set("retainedEarnings", d[RETAINED_EARNING] === "WITH");
    set("budgets", d[BUDGET] === "WITH_BUDGET");
    if (d[BUDGET] === "WITH_BUDGET") set("communityBudgetId", d[BUDGET_VERSION]);
    set("objectType", d[OBJECT_TYPE]);
    // Community: the community itself is the object, no id filter
    if (d[OBJECT_TYPE] !== "COMMUNITY") asArray(d[OBJECT_VALUES]).forEach((v) => p.append("objectValues", v));
    set("reportLevel", d[REPORTING_LEVEL]);
    set("periodBasis", d[REPORTING_PERIOD]);
    set("fiscalYear", d[FISCAL_YEAR]);
    set("periodFrom", d[PERIOD_FROM]);
    set("periodTo", d[PERIOD_TO]);
    asArray(d[DOCUMENT_TYPES]).forEach((v) => p.append("documentTypeIds", v));
    asArray(d[GL_MASTERS]).forEach((v) => p.append("glMasterIds", v));
    asArray(d[ACC_CATEGORY]).forEach((v) => p.append("accountCategoryIds", v));
    return p;
  };
  const { mutate: runReport, isPending: isRunning } = useMutation({
    mutationFn: async (d: any) => await axiosAuth.get(`${MANAGERIAL_REPORT_API}/community/${communityId}/run?${buildParams(d).toString()}`),
    onError: toastError,
    onSuccess: (res: any, d: any) => {
      setResult(res?.data?.data || null);
      setRanWith({ ...d });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });

  const validate = (d: any) => {
    const missing: string[] = [];
    if (!d[FINANCIAL_VERSION]) missing.push("Financial Version (publish one under Master Data > Financial Statement Versions)");
    if (d[BUDGET] === "WITH_BUDGET" && !d[BUDGET_VERSION]) missing.push(`Budget version (no approved budget for ${d[FISCAL_YEAR]})`);
    if (!d[OBJECT_TYPE]) missing.push("Object Type");
    if (!d[REPORTING_LEVEL]) missing.push("Report Level");
    if (!d[FISCAL_YEAR]) missing.push("Fiscal Year");
    if (!d[PERIOD_FROM] || !d[PERIOD_TO]) missing.push("Period From / To");
    if (missing.length) {
      toast.error(`Select: ${missing.join(", ")}`);
      return false;
    }
    return true;
  };
  const onSubmit = (d: any) => {
    if (validate(d)) runReport(d);
  };

  // ---- variants ----
  const { data: variantData, isFetching: isVariantLoading, refetch: refetchVariant } = useGetFilterVariantsById({
    id: loadVariantId,
    queryParams: { enabled: !!loadVariantId, staleTime: 0 },
  });
  useEffect(() => {
    if (!variantData) return;
    const c = variantData?.data?.criteria || variantData?.data || {};
    const base = defaultCriteria(communityId, currentFinanceYear);
    const str = (v: any, fallback: string) => (v === undefined || v === null || v === "" ? fallback : String(v));
    form.reset({
      ...base,
      [FINANCIAL_VERSION]: str(c[FINANCIAL_VERSION], ""),
      [REPORT_EXPANSION]: str(c[REPORT_EXPANSION], base[REPORT_EXPANSION]),
      [SCOPE]: str(c[SCOPE], base[SCOPE]),
      [UNIT_DISPLAY]: str(c[UNIT_DISPLAY], base[UNIT_DISPLAY]),
      // older variants stored booleans for these two
      [RETAINED_EARNING]: c[RETAINED_EARNING] === true || c[RETAINED_EARNING] === "WITH" ? "WITH" : "WITHOUT",
      [BUDGET]: c[BUDGET] === false || c[BUDGET] === "WITHOUT_BUDGET" ? "WITHOUT_BUDGET" : "WITH_BUDGET",
      [BUDGET_VERSION]: str(c[BUDGET_VERSION] ?? c.budgetVersion, ""),
      [DOWNLOAD_REPORT]: str(c[DOWNLOAD_REPORT], base[DOWNLOAD_REPORT]),
      [OBJECT_TYPE]: str(c[OBJECT_TYPE], base[OBJECT_TYPE]),
      [OBJECT_VALUES]: asArray(c[OBJECT_VALUES]),
      [REPORTING_LEVEL]: str(c[REPORTING_LEVEL], base[REPORTING_LEVEL]),
      [REPORTING_PERIOD]: str(c[REPORTING_PERIOD] ?? c.reportPeriod, base[REPORTING_PERIOD]),
      [FISCAL_YEAR]: str(c[FISCAL_YEAR], base[FISCAL_YEAR]),
      [PERIOD_FROM]: str(c[PERIOD_FROM], base[PERIOD_FROM]),
      [PERIOD_TO]: str(c[PERIOD_TO], base[PERIOD_TO]),
      [DOCUMENT_TYPES]: asArray(c[DOCUMENT_TYPES]),
      [GL_MASTERS]: asArray(c[GL_MASTERS]),
      [ACC_CATEGORY]: asArray(c[ACC_CATEGORY]),
    });
    previousObjectType.current = str(c[OBJECT_TYPE], base[OBJECT_TYPE]);
    setVariantName(variantData?.data?.name || "");
    toastSuccess(`Variant "${variantData?.data?.name || ""}" loaded`);
  }, [variantData]);

  const { mutate: addInfo, isPending: isAddPending } = useAddFilterVariantsMutation({
    onError: toastError,
    onSuccess: (res: any) => {
      selectedVariantId.current = res?.data?.data?.managerialReportFilterVariantId ?? selectedVariantId.current;
      toastSuccess("Variant saved");
      queryClient.invalidateQueries({ queryKey: ["managerial-filter-variant-list"] });
      setOpenSaveDialog(false);
    },
  });
  const { mutate: updateInfo, isPending: isUpdatePending } = useUpdateFilterVariantsMutation({
    onError: toastError,
    onSuccess: () => {
      toastSuccess("Variant updated");
      queryClient.invalidateQueries({ queryKey: ["managerial-filter-variant-list"] });
      setOpenSaveDialog(false);
    },
  });

  const criteria = () => ({ ...form.getValues() });
  const onSave = () => {
    if (!variantName.trim()) {
      toast.error("Enter a variant name");
      return;
    }
    addInfo({ data: { communityId, name: variantName.trim(), criteria: criteria() } });
  };
  const onUpdate = () => updateInfo({ id: selectedVariantId.current, data: { criteria: criteria() } });
  const onReset = () => {
    form.reset(defaultCriteria(communityId, currentFinanceYear));
    previousObjectType.current = "COMMUNITY";
    selectedVariantId.current = null;
    setLoadVariantId(null);
    setVariantName("");
  };
  const onLoadVariant = () => setOpenLoadVariantDialog(true);
  const onLoadConfirm = (id: any) => {
    selectedVariantId.current = id;
    setOpenLoadVariantDialog(false);
    if (id === loadVariantId) refetchVariant();
    else setLoadVariantId(id);
  };
  const onSaveBtnClick = () => setOpenSaveDialog(true);

  // labels for the "Filters Applied" strip and the download
  const labelOf = (options: any[], v: any) => options.find((o: any) => String(o.value) === String(v))?.label;
  const appliedLabels = (d: any) => ({
    financialVersion: labelOf(financialVersionOptions, d[FINANCIAL_VERSION]),
    budgetVersion: d[BUDGET] === "WITH_BUDGET" ? labelOf(budgetVersionOptions, d[BUDGET_VERSION]) : "Without budgets",
    objectValues: d[OBJECT_TYPE] === "COMMUNITY" ? communityName
      : asArray(d[OBJECT_VALUES]).map((v) => labelOf(objectValues, v) || v).join(", ") || `All ${String(d[OBJECT_TYPE]).toLowerCase()}s`,
    documentTypes: asArray(d[DOCUMENT_TYPES]).map((v) => {
      const dt = (docTypeList || []).find((x: any) => String(x.documentTypeId) === v);
      return dt ? dt.documentType : v;
    }).join(", "),
    glAccounts: asArray(d[GL_MASTERS]).map((v) => (glData?.data || []).find((g: any) => String(g.glMasterId) === v)?.glAccountNumber || v).join(", "),
    accountCategories: asArray(d[ACC_CATEGORY]).map((v) => labelOf(accountCategoryOptions, v) || v).join(", "),
  });

  return {
    breadcrumbData,
    currentFinanceYear,
    communityName,
    form,
    formData,
    financialVersionOptions,
    budgetVersionOptions,
    accountCategoryOptions,
    reportLevelOptions,
    docTypeList: docTypeList || [],
    glMasterList: (glData?.data || []).filter((g: any) => g.active !== false),
    isLoading: isLoadingDocTypeList || isLoadingGlMasterList || isVariantLoading || isBudgetDataLoading || isLoadingVersions,
    objectValues,
    onReset,
    onLoadVariant,
    openLoadVariantDialog,
    setOpenLoadVariantDialog,
    onLoadConfirm,
    openSaveDialog,
    setOpenSaveDialog,
    variantName,
    setVariantName,
    onSave,
    onUpdate,
    selectedVariantId: selectedVariantId.current,
    isAddPending,
    isUpdatePending,
    onSubmit,
    onSaveBtnClick,
    isLoadingDropdown: isLoadingZoneList || isLoadingBlockList || isLoadingPropertyList,
    isRunning,
    result,
    ranWith,
    appliedLabels: ranWith ? appliedLabels(ranWith) : null,
    onEditCriteria: () => setResult(null),
  };
};

export default useManagerialReport;
